import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

import { projectService } from '../../../services/projectService';

const DefaultPhasesTab = () => {
  const [phases, setPhases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [isAdding, setIsAdding] = useState(false);
  const [newPhase, setNewPhase] = useState({ name: '', description: '' });
  const [notification, setNotification] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadPhases();
  }, []);

  const loadPhases = async () => {
    setLoading(true);
    try {
      const { data } = await projectService?.getAllDefaultPhases();
      setPhases(data || []);
    } catch (err) {
      showNotification('Failed to load default phases', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3500);
  };

  const handleStartEdit = (phase) => {
    setEditingId(phase?.id);
    setEditForm({ name: phase?.name || '', description: phase?.description || '' });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleSaveEdit = async (id) => {
    if (!editForm?.name?.trim()) return;
    setSaving(true);
    try {
      const { error } = await projectService?.updateDefaultPhase(id, {
        name: editForm?.name?.trim(),
        description: editForm?.description?.trim() || ''
      });
      if (error) throw error;
      await loadPhases();
      setEditingId(null);
      showNotification('Phase updated successfully');
    } catch (err) {
      showNotification(err?.message || 'Failed to update phase', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this default phase? This will not affect existing project phases.')) return;
    try {
      const { error } = await projectService?.deleteDefaultPhase(id);
      if (error) throw error;
      await loadPhases();
      showNotification('Phase deleted');
    } catch (err) {
      showNotification(err?.message || 'Failed to delete phase', 'error');
    }
  };

  const handleAddPhase = async () => {
    if (!newPhase?.name?.trim()) return;
    setSaving(true);
    try {
      const maxOrder = phases?.reduce((max, p) => Math.max(max, p?.sortOrder || 0), 0);
      const { error } = await projectService?.createDefaultPhase({
        name: newPhase?.name?.trim(),
        description: newPhase?.description?.trim() || '',
        sortOrder: maxOrder + 1,
        isActive: true
      });
      if (error) throw error;
      await loadPhases();
      setNewPhase({ name: '', description: '' });
      setIsAdding(false);
      showNotification('Default phase added');
    } catch (err) {
      showNotification(err?.message || 'Failed to add phase', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleMoveUp = async (index) => {
    if (index === 0) return;
    const reordered = [...phases];
    [reordered[index - 1], reordered[index]] = [reordered?.[index], reordered?.[index - 1]];
    setPhases(reordered);
    await projectService?.reorderDefaultPhases(reordered?.map(p => p?.id));
  };

  const handleMoveDown = async (index) => {
    if (index === phases?.length - 1) return;
    const reordered = [...phases];
    [reordered[index], reordered[index + 1]] = [reordered?.[index + 1], reordered?.[index]];
    setPhases(reordered);
    await projectService?.reorderDefaultPhases(reordered?.map(p => p?.id));
  };

  return (
    <div className="space-y-6">
      {notification && (
        <div className={`flex items-center gap-3 px-4 py-3 rounded-lg border text-sm font-medium ${
          notification?.type === 'success' ?'bg-green-50 border-green-200 text-green-800' :'bg-red-50 border-red-200 text-red-800'
        }`}>
          <Icon name={notification?.type === 'success' ? 'CheckCircle2' : 'AlertCircle'} size={16} />
          {notification?.message}
        </div>
      )}

      <div>
        <h3 className="text-base font-semibold text-gray-900 mb-1">Default Project Phases</h3>
        <p className="text-sm text-gray-500">
          These phases are applied to new projects by default. Each project can customize its own phases after creation.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
        </div>
      ) : (
        <div className="space-y-2">
          {phases?.map((phase, index) => (
            <div
              key={phase?.id}
              className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200 group"
            >
              {/* Order controls */}
              <div className="flex flex-col gap-0.5 mt-0.5">
                <button
                  onClick={() => handleMoveUp(index)}
                  disabled={index === 0}
                  className="p-0.5 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="Move up"
                >
                  <Icon name="ChevronUp" size={14} />
                </button>
                <button
                  onClick={() => handleMoveDown(index)}
                  disabled={index === phases?.length - 1}
                  className="p-0.5 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="Move down"
                >
                  <Icon name="ChevronDown" size={14} />
                </button>
              </div>

              {/* Order badge */}
              <div className="flex-shrink-0 w-7 h-7 rounded-full bg-accent/10 text-accent flex items-center justify-center text-xs font-bold mt-0.5">
                {index + 1}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                {editingId === phase?.id ? (
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={editForm?.name || ''}
                      onChange={(e) => setEditForm(prev => ({ ...prev, name: e?.target?.value }))}
                      placeholder="Phase name"
                      className="w-full px-3 py-1.5 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
                      autoFocus
                    />
                    <input
                      type="text"
                      value={editForm?.description || ''}
                      onChange={(e) => setEditForm(prev => ({ ...prev, description: e?.target?.value }))}
                      placeholder="Description (optional)"
                      className="w-full px-3 py-1.5 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
                    />
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="default" onClick={() => handleSaveEdit(phase?.id)} disabled={saving || !editForm?.name?.trim()}>
                        {saving ? 'Saving...' : 'Save'}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={handleCancelEdit} disabled={saving}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{phase?.name}</p>
                    {phase?.description && (
                      <p className="text-xs text-gray-500 mt-0.5">{phase?.description}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Actions */}
              {editingId !== phase?.id && (
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => handleStartEdit(phase)}
                    className="p-1.5 rounded hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-colors"
                    title="Edit phase"
                  >
                    <Icon name="Edit2" size={14} />
                  </button>
                  <button
                    onClick={() => handleDelete(phase?.id)}
                    className="p-1.5 rounded hover:bg-red-100 text-gray-400 hover:text-red-600 transition-colors"
                    title="Delete phase"
                  >
                    <Icon name="Trash2" size={14} />
                  </button>
                </div>
              )}
            </div>
          ))}

          {/* Add new phase */}
          {isAdding ? (
            <div className="p-4 bg-accent/5 rounded-lg border border-accent/20 space-y-2">
              <input
                type="text"
                value={newPhase?.name || ''}
                onChange={(e) => setNewPhase(prev => ({ ...prev, name: e?.target?.value }))}
                placeholder="Phase name (e.g., Kick-Off)"
                className="w-full px-3 py-1.5 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
                autoFocus
                onKeyDown={(e) => e?.key === 'Enter' && handleAddPhase()}
              />
              <input
                type="text"
                value={newPhase?.description || ''}
                onChange={(e) => setNewPhase(prev => ({ ...prev, description: e?.target?.value }))}
                placeholder="Description (optional)"
                className="w-full px-3 py-1.5 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
              />
              <div className="flex items-center gap-2">
                <Button size="sm" variant="default" onClick={handleAddPhase} disabled={saving || !newPhase?.name?.trim()}>
                  {saving ? 'Adding...' : 'Add Phase'}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setIsAdding(false); setNewPhase({ name: '', description: '' }); }}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setIsAdding(true)}
              className="w-full flex items-center justify-center gap-2 p-3 rounded-lg border-2 border-dashed border-gray-300 text-gray-500 hover:border-accent hover:text-accent transition-colors text-sm font-medium"
            >
              <Icon name="Plus" size={16} />
              Add Default Phase
            </button>
          )}
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">How Default Phases Work</p>
            <ul className="space-y-1 text-blue-700 list-disc list-inside">
              <li>These phases are the starting template for all new projects</li>
              <li>When managing a project, you can add, edit, or remove phases for that specific project</li>
              <li>Changes here do not affect phases already assigned to existing projects</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DefaultPhasesTab;
